<template>
  <div class="analytics-page container-fluid px-0">
    <div class="card shadow-sm border-0 mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <h5 class="mb-0">
            <i class="bi bi-graph-up-arrow me-2"></i>
            Business Analytics
          </h5>
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="isAnyLoading"
            @click="fetchAllAnalytics"
          >
            <span
              v-if="isAnyLoading"
              class="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>
            Refresh all
          </button>
        </div>

        <div class="row gy-2 gx-2 align-items-end">
          <div class="col-12 col-md-3 col-xl-2">
            <label class="form-label mb-1">From</label>
            <input v-model="filters.from" type="date" class="form-control form-control-sm" />
          </div>

          <div class="col-12 col-md-3 col-xl-2">
            <label class="form-label mb-1">To</label>
            <input v-model="filters.to" type="date" class="form-control form-control-sm" />
          </div>

          <div class="col-12 col-md-4 col-xl-3">
            <label class="form-label mb-1">Device</label>
            <select v-model="filters.device_id" class="form-select form-select-sm">
              <option value="">All devices</option>
              <option v-for="device in devices" :key="device.id" :value="device.id">
                {{ device.name ?? `Device #${device.id}` }}
              </option>
            </select>
          </div>

          <div class="col-12 col-md-2 col-xl-5 d-flex gap-2 justify-content-md-end flex-wrap">
            <button class="btn btn-warning btn-sm px-3" @click="fetchAllAnalytics">
              Apply
            </button>
            <button class="btn btn-outline-secondary btn-sm px-3" @click="resetFilters">
              Clear
            </button>
          </div>
        </div>

        <div class="small text-muted mt-2">
          Default range is last 7 days through today.
        </div>
        <div v-if="devicesError" class="small text-danger mt-1">
          {{ devicesError }}
        </div>
      </div>
    </div>

    <PerformanceSnapshotBar
      :loading="executiveSummary.loading"
      :error-message="executiveSummary.error || ''"
      :snapshot="executiveSummary.data?.snapshot || null"
      :trend-indicator="executiveSummary.data?.trend_indicator || null"
      :calculation="executiveSummary.data?.calculation || null"
      :audit="executiveSummary.data?.audit || null"
    />

    <div class="row g-3">
      <div class="col-12">
        <AnalyticsSectionCard
          title="Revenue Timeline"
          :range-text="getRangeText(sections.revenueTimeline.data?.range)"
          :loading="sections.revenueTimeline.loading"
          :error="sections.revenueTimeline.error || ''"
          @info="openInfoModal('revenueTimeline', 'Revenue Timeline')"
          @refresh="fetchRevenueTimeline()"
        >
          <template #header-actions>
            <select
              v-model="timelineGranularity"
              class="form-select form-select-sm"
              style="width: 130px"
              @change="fetchRevenueTimeline()"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>

            <select v-model="timelineChartMode" class="form-select form-select-sm" style="width: 130px">
              <option value="line">Line chart</option>
              <option value="bar">Bar chart</option>
            </select>
          </template>

          <TrendIndicatorRows
            :rows="trendRowsBySection.revenueTimeline"
            :tooltip="getTrendTooltip('revenueTimeline')"
            key-prefix="trend-revenue"
          />

          <div v-if="revenueTimelinePoints.length">
            <RevenueTimelineChart
              :points="revenueTimelinePoints"
              :mode="timelineChartMode"
            />
          </div>
          <div v-else class="analytics-empty">No data</div>

          <div class="mt-4">
            <div class="ranking-toolbar">
              <div class="ranking-toolbar-left">
                <h6 class="mb-0">Device Ranking</h6>
                <span v-if="revenueTimelineRankingRows.length" class="small text-muted">
                  {{ formatInt(revenueTimelineRankingRows.length) }} devices
                </span>
              </div>
              <div class="d-flex align-items-center gap-2 ranking-toggle-wrap">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary ranking-expand-btn"
                  :disabled="!revenueTimelineRankingRows.length"
                  :aria-expanded="rankingExpanded ? 'true' : 'false'"
                  @click="rankingExpanded = !rankingExpanded"
                >
                  <i class="bi me-1" :class="rankingExpanded ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  {{ rankingExpanded ? "Collapse" : "Expand" }}
                </button>
                <template v-if="rankingExpanded">
                  <span class="small text-muted">Sparkline:</span>
                  <div class="btn-group btn-group-sm ranking-toggle" role="group" aria-label="Sparkline metric">
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="rankingSparklineMetric === 'revenue' ? 'btn-warning' : 'btn-outline-secondary'"
                      @click="rankingSparklineMetric = 'revenue'"
                    >
                      Revenue
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="rankingSparklineMetric === 'photos' ? 'btn-warning' : 'btn-outline-secondary'"
                      @click="rankingSparklineMetric = 'photos'"
                    >
                      Photos
                    </button>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="revenueTimelineRankingRows.length && rankingExpanded" class="device-ranking-list">
              <div
                v-for="row in revenueTimelineRankingRows"
                :key="row.device.device_id"
                class="device-ranking-item"
                :class="{
                  'device-ranking-leader': row.isGrowingLeader,
                  'device-ranking-attention': row.isNeedsAttention,
                }"
              >
                <div class="ranking-header-row">
                  <div class="ranking-device-block">
                    <span class="rank-badge" :class="getRankBadgeClass(row.rank)">
                      {{ row.rank }}
                    </span>
                    <div>
                      <div class="ranking-device-name">{{ row.device.device_name || "N/A" }}</div>
                      <div class="ranking-device-id">Device #{{ row.device.device_id }}</div>
                    </div>
                  </div>
                  <div class="ranking-totals-block">
                    <div class="ranking-total-revenue">{{ formatMoney(row.device.revenue) }} GEL</div>
                    <div class="ranking-total-photos">{{ formatInt(row.device.photos) }} photos</div>
                  </div>
                </div>

                <div class="ranking-metrics-row">
                  <span class="ranking-metric-pill">
                    Revenue/day
                    <strong>{{ formatMoney(row.device.revenue_per_active_day) }} GEL</strong>
                  </span>
                  <span class="ranking-metric-pill">
                    Photos/day
                    <strong>{{ formatDecimal(row.device.avg_photos_per_day) }}</strong>
                  </span>
                  <span class="ranking-metric-pill">
                    Active days
                    <strong>{{ formatInt(row.device.active_days) }}</strong>
                  </span>
                </div>

                <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
                  <span class="growth-label">Revenue trend:</span>
                  <span
                    class="growth-badge"
                    :class="getGrowthBadgeClass(row.revenueGrowth?.direction)"
                    :title="getTrendTooltip('revenueTimeline')"
                  >
                    {{ row.growthLabel }}
                  </span>
                  <span v-if="row.isGrowingLeader" class="rank-chip rank-chip-success">Growing leader</span>
                  <span v-if="row.isNeedsAttention" class="rank-chip rank-chip-warning">Needs attention</span>
                </div>

                <div class="ranking-sparkline-wrap mt-2">
                  <svg
                    v-if="row.sparkline.hasData"
                    class="ranking-sparkline"
                    viewBox="0 0 240 44"
                    preserveAspectRatio="none"
                  >
                    <polygon
                      :points="row.sparkline.areaPoints"
                      :fill="getSparklineAreaColor(row.revenueGrowth?.direction)"
                    />
                    <polyline
                      :points="row.sparkline.polylinePoints"
                      fill="none"
                      :stroke="getSparklineStrokeColor(row.revenueGrowth?.direction)"
                      stroke-width="2.2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <g v-for="(point, index) in row.sparkline.points" :key="`${row.device.device_id}-spark-${index}`">
                      <circle
                        class="ranking-sparkline-hitpoint"
                        :cx="point.x"
                        :cy="point.y"
                        r="5"
                      >
                        <title>{{ point.tooltip }}</title>
                      </circle>
                    </g>
                  </svg>
                  <div v-else class="small text-muted">No sparkline data</div>
                </div>
              </div>
            </div>
            <div v-else-if="revenueTimelineRankingRows.length" class="ranking-collapsed-state">
              Ranking is collapsed. Click Expand to view device list.
            </div>
            <div v-else class="analytics-empty analytics-empty-inline">
              No device data for selected period
            </div>
          </div>
        </AnalyticsSectionCard>
      </div>

      <div class="col-12 col-xl-6">
        <AnalyticsSectionCard
          title="Payment Split"
          :range-text="getRangeText(sections.paymentSplit.data?.range)"
          :loading="sections.paymentSplit.loading"
          :error="sections.paymentSplit.error || ''"
          @info="openInfoModal('paymentSplit', 'Payment Split')"
          @refresh="fetchPaymentSplit()"
        >
          <TrendIndicatorRows
            :rows="trendRowsBySection.paymentSplit"
            :tooltip="getTrendTooltip('paymentSplit')"
            key-prefix="trend-payment"
          />

          <div class="row g-2 mb-3">
            <div class="col-12 col-md-6">
              <div class="summary-box">
                <div class="summary-title">Photos</div>
                <div class="summary-value">{{ formatInt(paymentPhotos.total) }}</div>
                <div class="small text-muted">Physical: {{ formatInt(paymentPhotos.coin) }} ({{ formatPct(paymentPhotos.coin_pct) }})</div>
                <div class="small text-muted">Card: {{ formatInt(paymentPhotos.card) }} ({{ formatPct(paymentPhotos.card_pct) }})</div>
                <div class="small text-muted">Unknown: {{ formatInt(paymentPhotos.unknown) }}</div>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="summary-box">
                <div class="summary-title">Revenue</div>
                <div class="summary-value">{{ formatMoney(paymentRevenue.total) }}</div>
                <div class="small text-muted">Physical: {{ formatMoney(paymentRevenue.coin) }} ({{ formatPct(paymentRevenue.coin_pct) }})</div>
                <div class="small text-muted">Card: {{ formatMoney(paymentRevenue.card) }} ({{ formatPct(paymentRevenue.card_pct) }})</div>
                <div class="small text-muted">Unknown: {{ formatMoney(paymentRevenue.unknown) }}</div>
              </div>
            </div>
          </div>

          <PaymentSplitTrendChart v-if="paymentTrend.length" :trend="paymentTrend" />
          <div v-else class="analytics-empty">No data</div>
        </AnalyticsSectionCard>
      </div>

      <div class="col-12 col-xl-6">
        <AnalyticsSectionCard
          title="Top Templates"
          :range-text="getRangeText(sections.topTemplates.data?.range)"
          :loading="sections.topTemplates.loading"
          :error="sections.topTemplates.error || ''"
          refresh-label="Apply"
          @info="openInfoModal('topTemplates', 'Top Templates')"
          @refresh="fetchTopTemplates()"
        >
          <template #header-actions>
            <select v-model="templateMetric" class="form-select form-select-sm" style="width: 130px">
              <option value="revenue">Revenue</option>
              <option value="photos">Photos</option>
            </select>
            <input
              v-model.number="templateLimit"
              type="number"
              min="1"
              max="50"
              class="form-control form-control-sm"
              style="width: 90px"
            />
          </template>

          <TrendIndicatorRows
            :rows="trendRowsBySection.topTemplates"
            :tooltip="getTrendTooltip('topTemplates')"
            key-prefix="trend-top-templates"
          />

          <div v-if="topTemplates.length" class="table-responsive">
            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Template</th>
                  <th class="text-end">Photos</th>
                  <th class="text-end">Revenue</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="template in topTemplates" :key="template.template_id ?? template.template_name">
                  <td>
                    <div class="fw-semibold">{{ template.template_name }}</div>
                    <div class="small text-muted">ID: {{ template.template_id ?? "N/A" }}</div>
                  </td>
                  <td class="text-end">{{ formatInt(template.photos) }}</td>
                  <td class="text-end">{{ formatMoney(template.revenue) }}</td>
                  <td>
                    <div class="small mb-1">Photo {{ formatPct(template.photo_share_pct) }}</div>
                    <div class="progress mb-1 share-progress">
                      <div
                        class="progress-bar bg-info"
                        role="progressbar"
                        :style="{ width: getShareWidth(template.photo_share_pct) }"
                      ></div>
                    </div>
                    <div class="small mb-1">Revenue {{ formatPct(template.revenue_share_pct) }}</div>
                    <div class="progress share-progress">
                      <div
                        class="progress-bar bg-success"
                        role="progressbar"
                        :style="{ width: getShareWidth(template.revenue_share_pct) }"
                      ></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="analytics-empty">No data</div>
        </AnalyticsSectionCard>
      </div>

      <div class="col-12">
        <AnalyticsSectionCard
          title="Hourly Map"
          :range-text="getRangeText(sections.hourlyMap.data?.range)"
          :loading="sections.hourlyMap.loading"
          :error="sections.hourlyMap.error || ''"
          @info="openInfoModal('hourlyMap', 'Hourly Map')"
          @refresh="fetchHourlyMap()"
        >
          <template #header-actions>
            <select v-model="heatmapMetric" class="form-select form-select-sm" style="width: 140px">
              <option value="revenue">Heatmap: Revenue</option>
              <option value="photos">Heatmap: Photos</option>
            </select>
          </template>

          <TrendIndicatorRows
            :rows="trendRowsBySection.hourlyMap"
            :tooltip="getTrendTooltip('hourlyMap')"
            key-prefix="trend-hourly"
          />

          <div class="mb-4">
            <h6 class="mb-2">Hourly Totals</h6>
            <HourlyTotalsChart v-if="hourlyRows.length" :hours="hourlyRows" />
            <div v-else class="analytics-empty analytics-empty-inline">No data</div>
          </div>
          <div>
            <h6 class="mb-2">Date/Hour Heatmap</h6>
            <HourlyHeatmap v-if="hourlyHeatmap.length" :heatmap="hourlyHeatmap" :metric="heatmapMetric" />
            <div v-else class="analytics-empty analytics-empty-inline">No data</div>
          </div>
        </AnalyticsSectionCard>
      </div>

      <div class="col-12 col-xl-4">
        <AnalyticsSectionCard
          title="Top Hours"
          :range-text="getRangeText(sections.topHours.data?.range)"
          :loading="sections.topHours.loading"
          :error="sections.topHours.error || ''"
          @info="openInfoModal('topHours', 'Top Hours')"
          @refresh="fetchTopHours()"
        >
          <TrendIndicatorRows
            :rows="trendRowsBySection.topHours"
            :tooltip="getTrendTooltip('topHours')"
            key-prefix="trend-top-hours"
          />

          <div class="mb-4">
            <h6 class="mb-2">Top 3 by Revenue</h6>
            <ul v-if="topRevenueHours.length" class="list-group list-group-flush">
              <li
                v-for="item in topRevenueHours"
                :key="`rev-${item.hour}`"
                class="list-group-item px-0 d-flex justify-content-between align-items-center"
              >
                <span>
                  <strong>{{ item.label }}</strong>
                  <span class="small text-muted d-block">{{ formatInt(item.photos) }} photos</span>
                </span>
                <span class="fw-semibold">{{ formatMoney(item.revenue) }}</span>
              </li>
            </ul>
            <div v-else class="analytics-empty analytics-empty-inline">No data</div>
          </div>

          <div>
            <h6 class="mb-2">Top 3 by Photos</h6>
            <ul v-if="topPhotoHours.length" class="list-group list-group-flush">
              <li
                v-for="item in topPhotoHours"
                :key="`photo-${item.hour}`"
                class="list-group-item px-0 d-flex justify-content-between align-items-center"
              >
                <span>
                  <strong>{{ item.label }}</strong>
                  <span class="small text-muted d-block">{{ formatMoney(item.revenue) }} revenue</span>
                </span>
                <span class="fw-semibold">{{ formatInt(item.photos) }}</span>
              </li>
            </ul>
            <div v-else class="analytics-empty analytics-empty-inline">No data</div>
          </div>
        </AnalyticsSectionCard>
      </div>

      <div class="col-12 col-xl-8">
        <AnalyticsSectionCard
          title="Price Insight"
          :loading="sections.priceInsight.loading"
          :error="sections.priceInsight.error || ''"
          @info="openInfoModal('priceInsight', 'Price Insight')"
          @refresh="fetchPriceInsight()"
        >
          <template #range>
            {{ getRangeText(sections.priceInsight.data?.range) }}
            <span class="ms-1 text-uppercase">{{ sections.priceInsight.data?.method || "" }}</span>
          </template>

          <TrendIndicatorRows
            :rows="trendRowsBySection.priceInsight"
            :tooltip="getTrendTooltip('priceInsight')"
            key-prefix="trend-price-insight"
          />

          <div v-if="priceInsightDevices.length">
            <div v-if="priceInsightInsufficientDevices.length" class="price-insight-controls">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :aria-expanded="priceInsightShowInsufficient ? 'true' : 'false'"
                @click="priceInsightShowInsufficient = !priceInsightShowInsufficient"
              >
                <i class="bi me-1" :class="priceInsightShowInsufficient ? 'bi-eye-slash' : 'bi-eye'"></i>
                {{
                  priceInsightShowInsufficient
                    ? `Hide insufficient data (${formatInt(priceInsightInsufficientDevices.length)})`
                    : `Show insufficient data (${formatInt(priceInsightInsufficientDevices.length)})`
                }}
              </button>
            </div>

            <div
              v-for="device in priceInsightRenderableDevices"
              :key="device.device_id"
              class="price-insight-device border rounded p-3 mb-3"
            >
              <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                <div>
                  <div class="fw-semibold">#{{ device.device_id }} {{ device.device_name }}</div>
                  <div class="small text-muted">Current price: {{ formatMoney(device.current_price) }} GEL</div>
                </div>
                <span :class="['badge', insightBadgeClass(device.insight?.label)]">
                  {{ insightLabel(device.insight?.label) }}
                </span>
              </div>

              <div class="small text-muted mb-2">
                Price change:
                <span class="fw-semibold">{{ formatNullablePct(device.insight?.price_change_pct) }}</span>
                |
                Photo change:
                <span class="fw-semibold">{{ formatNullablePct(device.insight?.photo_change_pct) }}</span>
              </div>

              <div v-if="device.price_points?.length" class="table-responsive">
                <table class="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Price</th>
                      <th class="text-end">Photos</th>
                      <th class="text-end">Revenue</th>
                      <th class="text-end">Active Days</th>
                      <th class="text-end">Avg Photos/Day</th>
                      <th class="text-end">Avg Revenue/Day</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="point in device.price_points" :key="`${device.device_id}-${point.price_minor}`">
                      <td>{{ formatMoney(point.price) }}</td>
                      <td class="text-end">{{ formatInt(point.photos) }}</td>
                      <td class="text-end">{{ formatMoney(point.revenue) }}</td>
                      <td class="text-end">{{ formatInt(point.active_days) }}</td>
                      <td class="text-end">{{ formatDecimal(point.avg_photos_per_day) }}</td>
                      <td class="text-end">{{ formatMoney(point.avg_revenue_per_day) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="analytics-empty analytics-empty-inline">No data</div>
            </div>

            <div
              v-if="!priceInsightShowInsufficient && priceInsightInsufficientDevices.length"
              class="price-insight-collapsed-note"
            >
              <span v-if="priceInsightSufficientDevices.length">
                Devices with insufficient data are hidden. Click "Show insufficient data" to view them.
              </span>
              <span v-else>
                All devices in this range are marked as insufficient data. Click "Show insufficient data" to view them.
              </span>
            </div>
          </div>
          <div v-else class="analytics-empty">No data</div>
        </AnalyticsSectionCard>
      </div>
    </div>

    <BootstrapModal
      ref="calculationModal"
      modalId="analyticsCalculationModal"
      title="Methodology"
      :isLarge="true"
      :hideConfirmButton="true"
    >
      <template #body>
        <div class="calculation-modal-body">
          <div class="fw-semibold mb-3">{{ activeInfoTitle }}</div>

          <div v-if="!hasCalculationDetails" class="text-muted">
            Calculation details are not available
          </div>

          <div v-else>
            <div class="info-section">
              <h6 class="mb-2">Formula</h6>
              <ul class="mb-0 ps-3">
                <li v-for="(rule, index) in calculationRules" :key="`rule-${index}`">
                  {{ rule }}
                </li>
              </ul>
            </div>

            <div class="info-section">
              <h6 class="mb-2">Data source</h6>
              <div class="small mb-2">
                <span class="text-muted">Source:</span>
                <span class="ms-1">{{ stringifyValue(activeCalculation?.source) }}</span>
              </div>
              <div class="small">
                <span class="text-muted">Tables:</span>
                <span class="ms-1">{{ renderInlineList(activeAudit?.source_tables) }}</span>
              </div>
            </div>

            <div class="info-section">
              <h6 class="mb-2">Time window</h6>
              <div class="small mb-1">
                <span class="text-muted">Timezone:</span>
                <span class="ms-1">{{ stringifyValue(activeAudit?.timezone) }}</span>
              </div>
              <div class="small mb-1">
                <span class="text-muted">From:</span>
                <span class="ms-1">{{ stringifyValue(activeAudit?.date_window?.from) }}</span>
              </div>
              <div class="small mb-1">
                <span class="text-muted">To:</span>
                <span class="ms-1">{{ stringifyValue(activeAudit?.date_window?.to) }}</span>
              </div>
              <div class="small mb-1">
                <span class="text-muted">Start at:</span>
                <span class="ms-1">{{ stringifyValue(activeAudit?.date_window?.start_at) }}</span>
              </div>
              <div class="small">
                <span class="text-muted">End before:</span>
                <span class="ms-1">{{ stringifyValue(activeAudit?.date_window?.end_before) }}</span>
              </div>
            </div>

            <div class="info-section">
              <h6 class="mb-2">Filters</h6>
              <pre class="info-pre mb-0">{{ prettyJson(activeAudit?.filters) }}</pre>
            </div>

            <div class="info-section">
              <h6 class="mb-2">Audit</h6>
              <div class="small mb-1">
                <span class="text-muted">Formula version:</span>
                <span class="ms-1">{{ stringifyValue(activeAudit?.formula_version) }}</span>
              </div>
              <div class="small mb-1">
                <span class="text-muted">Minor unit divisor:</span>
                <span class="ms-1">{{ stringifyValue(activeAudit?.minor_unit_divisor) }}</span>
              </div>
              <div class="small mb-1">
                <span class="text-muted">Currency:</span>
                <span class="ms-1">{{ stringifyValue(activeAudit?.currency) }}</span>
              </div>
              <div v-if="activeAudit?.totals_minor !== undefined" class="mt-2">
                <div class="small text-muted mb-1">Totals (minor units)</div>
                <pre class="info-pre mb-0">{{ prettyJson(activeAudit?.totals_minor) }}</pre>
              </div>
              <div v-if="activeAudit?.checks !== undefined" class="mt-2">
                <div class="small text-muted mb-1">Checks</div>
                <pre class="info-pre mb-0">{{ prettyJson(activeAudit?.checks) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </template>
    </BootstrapModal>
  </div>
</template>

<script>
import analyticsService from "@/services/analyticsService";
import BootstrapModal from "@/components/Common/BootstrapModal.vue";
import AnalyticsSectionCard from "@/components/Analytics/AnalyticsSectionCard.vue";
import deviceService from "@/services/deviceService";
import HourlyHeatmap from "@/components/Analytics/HourlyHeatmap.vue";
import HourlyTotalsChart from "@/components/Analytics/HourlyTotalsChart.vue";
import PaymentSplitTrendChart from "@/components/Analytics/PaymentSplitTrendChart.vue";
import PerformanceSnapshotBar from "@/components/Analytics/PerformanceSnapshotBar.vue";
import RevenueTimelineChart from "@/components/Analytics/RevenueTimelineChart.vue";
import TrendIndicatorRows from "@/components/Analytics/TrendIndicatorRows.vue";

const buildSectionState = () => ({
  loading: false,
  error: null,
  data: null,
});

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const toDate = new Date();
  const fromDate = new Date(toDate);
  fromDate.setDate(fromDate.getDate() - 7);

  return {
    from: formatDateForInput(fromDate),
    to: formatDateForInput(toDate),
  };
};

export default {
  name: "SmartRevenueAnalytics",
  components: {
    AnalyticsSectionCard,
    BootstrapModal,
    HourlyHeatmap,
    HourlyTotalsChart,
    PaymentSplitTrendChart,
    PerformanceSnapshotBar,
    RevenueTimelineChart,
    TrendIndicatorRows,
  },
  data() {
    const defaultRange = getDefaultDateRange();

    return {
      filters: {
        from: defaultRange.from,
        to: defaultRange.to,
        device_id: "",
      },
      devices: [],
      devicesError: "",
      timelineGranularity: "day",
      timelineChartMode: "line",
      rankingSparklineMetric: "revenue",
      rankingExpanded: false,
      priceInsightShowInsufficient: false,
      templateMetric: "revenue",
      templateLimit: 10,
      heatmapMetric: "revenue",
      requestId: 0,
      executiveSummaryRequestId: 0,
      infoState: {
        title: "",
        payload: null,
      },
      executiveSummary: buildSectionState(),
      sections: {
        revenueTimeline: buildSectionState(),
        paymentSplit: buildSectionState(),
        hourlyMap: buildSectionState(),
        topHours: buildSectionState(),
        topTemplates: buildSectionState(),
        priceInsight: buildSectionState(),
      },
    };
  },
  computed: {
    isAnyLoading() {
      return Object.values(this.sections).some((section) => section.loading);
    },
    revenueTimelinePoints() {
      return this.sections.revenueTimeline.data?.points || [];
    },
    revenueTimelineDevices() {
      return this.sections.revenueTimeline.data?.devices || [];
    },
    revenueTimelineRankingRows() {
      return this.revenueTimelineDevices.map((device, index, list) => {
        const revenueGrowth = device?.growth_vs_previous_period?.revenue || null;

        return {
          rank: index + 1,
          device,
          revenueGrowth,
          growthLabel: this.getGrowthLabel(revenueGrowth),
          sparkline: this.buildDeviceSparkline(device),
          isGrowingLeader: index === 0 && revenueGrowth?.direction === "up",
          isNeedsAttention:
            index === list.length - 1 &&
            list.length > 1 &&
            revenueGrowth?.direction === "down",
        };
      });
    },
    paymentPhotos() {
      return this.sections.paymentSplit.data?.totals?.photos || {};
    },
    paymentRevenue() {
      return this.sections.paymentSplit.data?.totals?.revenue || {};
    },
    paymentTrend() {
      return this.sections.paymentSplit.data?.trend || [];
    },
    hourlyRows() {
      return this.sections.hourlyMap.data?.hours || [];
    },
    hourlyHeatmap() {
      return this.sections.hourlyMap.data?.heatmap || [];
    },
    topRevenueHours() {
      return this.sections.topHours.data?.top_revenue_hours || [];
    },
    topPhotoHours() {
      return this.sections.topHours.data?.top_photo_hours || [];
    },
    topTemplates() {
      return this.sections.topTemplates.data?.templates || [];
    },
    priceInsightDevices() {
      return this.sections.priceInsight.data?.devices || [];
    },
    priceInsightSufficientDevices() {
      return this.priceInsightDevices.filter(
        (device) => device?.insight?.label !== "insufficient_data"
      );
    },
    priceInsightInsufficientDevices() {
      return this.priceInsightDevices.filter(
        (device) => device?.insight?.label === "insufficient_data"
      );
    },
    priceInsightRenderableDevices() {
      if (this.priceInsightShowInsufficient) {
        return [
          ...this.priceInsightSufficientDevices,
          ...this.priceInsightInsufficientDevices,
        ];
      }

      return this.priceInsightSufficientDevices;
    },
    trendRowsBySection() {
      return {
        revenueTimeline: this.getTrendMetricRows("revenueTimeline"),
        paymentSplit: this.getTrendMetricRows("paymentSplit"),
        hourlyMap: this.getTrendMetricRows("hourlyMap"),
        topHours: this.getTrendMetricRows("topHours"),
        topTemplates: this.getTrendMetricRows("topTemplates"),
        priceInsight: this.getTrendMetricRows("priceInsight"),
      };
    },
    activeInfoTitle() {
      return this.infoState.title || "Analytics details";
    },
    activeCalculation() {
      return this.infoState.payload?.calculation || null;
    },
    activeAudit() {
      return this.infoState.payload?.audit || null;
    },
    hasCalculationDetails() {
      return !!(this.activeCalculation && this.activeAudit);
    },
    calculationRules() {
      if (!this.activeCalculation?.rules) {
        return [];
      }

      if (Array.isArray(this.activeCalculation.rules)) {
        return this.activeCalculation.rules;
      }

      return [String(this.activeCalculation.rules)];
    },
  },
  async mounted() {
    await this.fetchDevices();
    await this.fetchAllAnalytics();
  },
  methods: {
    async fetchDevices() {
      this.devicesError = "";

      try {
        const payload = await deviceService.getDevices();
        const rows = payload?.devices || [];
        this.devices = [...rows].sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      } catch (error) {
        this.devicesError = this.getErrorMessage(error);
        this.devices = [];
      }
    },
    resetFilters() {
      const defaultRange = getDefaultDateRange();
      this.filters.from = defaultRange.from;
      this.filters.to = defaultRange.to;
      this.filters.device_id = "";
    },
    buildBaseFilters() {
      const query = {};

      if (this.filters.from) {
        query.from = this.filters.from;
      }

      if (this.filters.to) {
        query.to = this.filters.to;
      }

      if (this.filters.device_id !== "") {
        query.device_id = Number(this.filters.device_id);
      }

      return query;
    },
    async runSection(sectionKey, requestId, fn) {
      const section = this.sections[sectionKey];
      section.loading = true;
      section.error = null;

      try {
        const payload = await fn();
        if (requestId !== this.requestId) {
          return;
        }
        section.data = payload;
      } catch (error) {
        if (requestId !== this.requestId) {
          return;
        }
        section.error = this.getErrorMessage(error);
        section.data = null;
      } finally {
        if (requestId === this.requestId) {
          section.loading = false;
        }
      }
    },
    async fetchAllAnalytics() {
      const currentId = Date.now();
      this.requestId = currentId;

      await Promise.allSettled([
        this.fetchExecutiveSummary(),
        this.fetchRevenueTimeline(currentId),
        this.fetchPaymentSplit(currentId),
        this.fetchHourlyMap(currentId),
        this.fetchTopHours(currentId),
        this.fetchTopTemplates(currentId),
        this.fetchPriceInsight(currentId),
      ]);
    },
    async fetchExecutiveSummary() {
      const activeRequestId = Date.now();
      this.executiveSummaryRequestId = activeRequestId;
      this.executiveSummary.loading = true;
      this.executiveSummary.error = null;

      try {
        const payload = await analyticsService.getExecutiveSummary(this.buildBaseFilters());

        if (activeRequestId !== this.executiveSummaryRequestId) {
          return;
        }

        this.executiveSummary.data = payload || {};
      } catch (error) {
        if (activeRequestId !== this.executiveSummaryRequestId) {
          return;
        }

        const status = error?.status || error?.response?.status;

        if (status === 401 || status === 403) {
          this.executiveSummary.error = "Not authorized";
        } else if (status === 422) {
          this.executiveSummary.error = "Invalid date range";
        } else {
          this.executiveSummary.error = this.getErrorMessage(error);
        }

        this.executiveSummary.data = {};
      } finally {
        if (activeRequestId === this.executiveSummaryRequestId) {
          this.executiveSummary.loading = false;
        }
      }
    },
    fetchRevenueTimeline(requestId = null) {
      const activeRequestId = requestId || Date.now();
      if (!requestId) {
        this.requestId = activeRequestId;
      }

      const query = {
        ...this.buildBaseFilters(),
        granularity: this.timelineGranularity,
      };

      return this.runSection("revenueTimeline", activeRequestId, () =>
        analyticsService.getRevenueTimeline(query)
      );
    },
    fetchPaymentSplit(requestId = null) {
      const activeRequestId = requestId || Date.now();
      if (!requestId) {
        this.requestId = activeRequestId;
      }

      const query = this.buildBaseFilters();
      return this.runSection("paymentSplit", activeRequestId, () =>
        analyticsService.getPaymentSplit(query)
      );
    },
    fetchHourlyMap(requestId = null) {
      const activeRequestId = requestId || Date.now();
      if (!requestId) {
        this.requestId = activeRequestId;
      }

      const query = this.buildBaseFilters();
      return this.runSection("hourlyMap", activeRequestId, () =>
        analyticsService.getHourlyMap(query)
      );
    },
    fetchTopHours(requestId = null) {
      const activeRequestId = requestId || Date.now();
      if (!requestId) {
        this.requestId = activeRequestId;
      }

      const query = this.buildBaseFilters();
      return this.runSection("topHours", activeRequestId, () =>
        analyticsService.getTopHours(query)
      );
    },
    fetchTopTemplates(requestId = null) {
      const activeRequestId = requestId || Date.now();
      if (!requestId) {
        this.requestId = activeRequestId;
      }

      const query = {
        ...this.buildBaseFilters(),
        metric: this.templateMetric,
        limit: this.templateLimit,
      };

      return this.runSection("topTemplates", activeRequestId, () =>
        analyticsService.getTopTemplates(query)
      );
    },
    fetchPriceInsight(requestId = null) {
      const activeRequestId = requestId || Date.now();
      if (!requestId) {
        this.requestId = activeRequestId;
      }
      this.priceInsightShowInsufficient = false;

      const query = this.buildBaseFilters();
      return this.runSection("priceInsight", activeRequestId, () =>
        analyticsService.getPriceInsight(query)
      );
    },
    openInfoModal(sectionKey, title) {
      this.infoState = {
        title,
        payload: this.sections[sectionKey]?.data || null,
      };

      this.$refs.calculationModal?.show();
    },
    getTrendMetricConfigs(sectionKey) {
      const topTemplatesMetric = this.sections.topTemplates.data?.metric || this.templateMetric;
      const map = {
        revenueTimeline: [
          { key: "revenue_total", label: "Revenue", unit: "money" },
          { key: "photo_count_total", label: "Photos", unit: "count" },
        ],
        paymentSplit: [
          { key: "revenue_total", label: "Revenue", unit: "money" },
          { key: "photo_count_total", label: "Photos", unit: "count" },
          { key: "coin_revenue_pct", label: "Physical Revenue Share", unit: "percentage" },
        ],
        hourlyMap: [
          { key: "revenue_total", label: "Revenue", unit: "money" },
          { key: "photo_count_total", label: "Photos", unit: "count" },
        ],
        topHours: [
          {
            key: "top_revenue_hours_total",
            label: "Top Revenue Hours",
            unit: "money",
            icon: "bi-cash-coin",
            iconTone: "trend-metric-icon-revenue",
          },
          {
            key: "top_photo_hours_total",
            label: "Top Photo Hours",
            unit: "count",
            icon: "bi-camera2",
            iconTone: "trend-metric-icon-photos",
          },
        ],
        topTemplates: [
          {
            key: "top_templates_total",
            label: "Top Templates Total",
            unit: topTemplatesMetric === "photos" ? "count" : "money",
          },
        ],
        priceInsight: [
          { key: "revenue_total", label: "Revenue", unit: "money" },
          { key: "photo_count_total", label: "Photos", unit: "count" },
        ],
      };

      return map[sectionKey] || [];
    },
    getTrendMetricRows(sectionKey) {
      const trend = this.sections[sectionKey]?.data?.trend_indicator;
      if (!trend?.metrics) {
        return [];
      }

      const configs = this.getTrendMetricConfigs(sectionKey);

      return configs
        .map((config) => {
          const metric = trend.metrics?.[config.key];
          if (!metric) {
            return null;
          }

          const unit = metric.unit || config.unit;
          const direction = metric.direction || "flat";
          const deltaPct = metric.delta_pct;

          return {
            key: config.key,
            label: config.label,
            currentText: this.formatTrendCurrent(metric.current, unit),
            direction,
            arrow: this.getTrendArrow(direction),
            icon: config.icon || "",
            iconTone: config.iconTone || "",
            hasBaseline: deltaPct !== null && deltaPct !== undefined,
            signedDeltaPct: this.formatSignedDeltaPct(deltaPct),
          };
        })
        .filter(Boolean);
    },
    getTrendArrow(direction) {
      if (direction === "up") {
        return "▲";
      }

      if (direction === "down") {
        return "▼";
      }

      return "■";
    },
    formatTrendCurrent(value, unit) {
      const numeric = Number(value || 0);

      if (unit === "money") {
        return `${numeric.toFixed(2)} GEL`;
      }

      if (unit === "percentage") {
        return `${numeric.toFixed(2)}%`;
      }

      return Math.round(numeric).toLocaleString();
    },
    formatSignedDeltaPct(deltaPct) {
      if (deltaPct === null || deltaPct === undefined) {
        return "N/A";
      }

      const numeric = Number(deltaPct);
      if (!Number.isFinite(numeric)) {
        return "N/A";
      }

      if (numeric > 0) {
        return `+${numeric.toFixed(2)}%`;
      }

      if (numeric < 0) {
        return `-${Math.abs(numeric).toFixed(2)}%`;
      }

      return `${numeric.toFixed(2)}%`;
    },
    getTrendTooltip(sectionKey) {
      const current = this.sections[sectionKey]?.data?.trend_indicator?.current_period;
      const previous = this.sections[sectionKey]?.data?.trend_indicator?.previous_period;
      if (!current?.from || !current?.to || !previous?.from || !previous?.to) {
        return "Compared to previous period: N/A";
      }

      return `Compared to previous period: ${previous.from} → ${previous.to}`;
    },
    getRankBadgeClass(rank) {
      if (rank === 1) {
        return "rank-badge-gold";
      }

      if (rank === 2) {
        return "rank-badge-silver";
      }

      if (rank === 3) {
        return "rank-badge-bronze";
      }

      return "rank-badge-default";
    },
    getGrowthLabel(growth) {
      if (!growth || growth.delta_pct === null || growth.delta_pct === undefined) {
        return "No previous baseline";
      }

      return `${this.getTrendArrow(growth.direction)} ${this.formatSignedDeltaPctCompact(growth.delta_pct)} vs previous period`;
    },
    formatSignedDeltaPctCompact(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return "N/A";
      }

      if (numeric > 0) {
        return `+${numeric.toFixed(1)}%`;
      }

      if (numeric < 0) {
        return `-${Math.abs(numeric).toFixed(1)}%`;
      }

      return `${numeric.toFixed(1)}%`;
    },
    getGrowthBadgeClass(direction) {
      if (direction === "up") {
        return "growth-badge-up";
      }

      if (direction === "down") {
        return "growth-badge-down";
      }

      return "growth-badge-flat";
    },
    getSparklineStrokeColor(direction) {
      if (this.rankingSparklineMetric === "photos") {
        return "#0d6efd";
      }

      if (direction === "up") {
        return "#198754";
      }

      if (direction === "down") {
        return "#dc3545";
      }

      return "#6c757d";
    },
    getSparklineAreaColor(direction) {
      if (this.rankingSparklineMetric === "photos") {
        return "rgba(13, 110, 253, 0.12)";
      }

      if (direction === "up") {
        return "rgba(25, 135, 84, 0.14)";
      }

      if (direction === "down") {
        return "rgba(220, 53, 69, 0.12)";
      }

      return "rgba(108, 117, 125, 0.12)";
    },
    buildDeviceSparkline(device) {
      const buckets = Array.isArray(device?.sparkline?.buckets)
        ? device.sparkline.buckets
        : [];
      const seriesSource = Array.isArray(device?.sparkline?.[this.rankingSparklineMetric])
        ? device.sparkline[this.rankingSparklineMetric]
        : [];

      const length = Math.max(buckets.length, seriesSource.length);
      if (!length) {
        return {
          hasData: false,
          points: [],
          polylinePoints: "",
        };
      }

      const values = Array.from({ length }, (_, index) => Number(seriesSource[index] || 0));
      const width = 240;
      const height = 44;
      const padding = 3;
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min || 1;
      const denominator = Math.max(length - 1, 1);

      const points = values.map((value, index) => {
        const x = padding + (index * (width - padding * 2)) / denominator;
        const y =
          height -
          padding -
          ((value - min) / range) * (height - padding * 2);
        const bucket = buckets[index] || `Bucket ${index + 1}`;
        const renderedValue =
          this.rankingSparklineMetric === "revenue"
            ? `${Number(value).toFixed(2)} GEL`
            : `${Math.round(Number(value || 0))}`;

        return {
          x: Number(x.toFixed(2)),
          y: Number(y.toFixed(2)),
          tooltip: `${bucket}: ${renderedValue}`,
        };
      });

      return {
        hasData: true,
        points,
        polylinePoints: points.map((point) => `${point.x},${point.y}`).join(" "),
        areaPoints: [
          `${points[0].x},${height - padding}`,
          ...points.map((point) => `${point.x},${point.y}`),
          `${points[points.length - 1].x},${height - padding}`,
        ].join(" "),
      };
    },
    getRangeText(range) {
      if (!range?.from || !range?.to) {
        return "Range: default";
      }

      return `Range: ${range.from} -> ${range.to}`;
    },
    getErrorMessage(error) {
      const raw = error?.customMessage || error?.originalMessage || error?.message;
      if (!raw) {
        return this.$t("errors.failed_to_fetch_data");
      }

      if (typeof raw !== "string") {
        return this.$t("errors.failed_to_fetch_data");
      }

      if (raw.startsWith("errors.")) {
        return this.$t(raw);
      }

      const translated = this.$t(`errors.${raw}`);
      if (translated && translated !== `errors.${raw}`) {
        return translated;
      }

      return raw;
    },
    formatInt(value) {
      return Number(value || 0).toLocaleString();
    },
    formatDecimal(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatPct(value) {
      return `${Number(value || 0).toFixed(1)}%`;
    },
    formatNullablePct(value) {
      if (value === null || value === undefined) {
        return "N/A";
      }

      return `${Number(value).toFixed(1)}%`;
    },
    getShareWidth(value) {
      const safeValue = Math.max(0, Math.min(100, Number(value || 0)));
      return `${safeValue}%`;
    },
    stringifyValue(value) {
      if (value === null || value === undefined || value === "") {
        return "N/A";
      }

      if (typeof value === "object") {
        return JSON.stringify(value);
      }

      return String(value);
    },
    renderInlineList(value) {
      if (value === null || value === undefined || value === "") {
        return "N/A";
      }

      if (Array.isArray(value)) {
        return value.length ? value.join(", ") : "N/A";
      }

      if (typeof value === "object") {
        return Object.entries(value)
          .map(([key, itemValue]) => `${key}: ${this.stringifyValue(itemValue)}`)
          .join(", ");
      }

      return String(value);
    },
    prettyJson(value) {
      if (value === null || value === undefined || value === "") {
        return "N/A";
      }

      if (typeof value === "string") {
        return value;
      }

      return JSON.stringify(value, null, 2);
    },
    insightLabel(label) {
      const labels = {
        insufficient_data: "Insufficient data",
        neutral: "Neutral",
        likely_price_sensitive: "Likely price sensitive",
        can_tolerate_higher_price: "Can tolerate higher price",
        demand_responded_to_discount: "Responded to discount",
      };

      return labels[label] || label || "N/A";
    },
    insightBadgeClass(label) {
      const classes = {
        insufficient_data: "text-bg-secondary",
        neutral: "text-bg-info",
        likely_price_sensitive: "text-bg-warning",
        can_tolerate_higher_price: "text-bg-success",
        demand_responded_to_discount: "text-bg-primary",
      };

      return classes[label] || "text-bg-secondary";
    },
  },
};
</script>

<style scoped>
.analytics-empty {
  min-height: 220px;
  border: 1px dashed #ced4da;
  border-radius: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #6c757d;
  font-size: 0.95rem;
}

.analytics-empty-inline {
  min-height: 90px;
}

.summary-box {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.ranking-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}

.ranking-toolbar-left {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.ranking-toggle-wrap {
  justify-content: flex-end;
  flex-wrap: wrap;
}

.ranking-expand-btn {
  min-width: 96px;
}

.ranking-toggle .btn {
  min-width: 72px;
}

.ranking-collapsed-state {
  border: 1px dashed #ced4da;
  border-radius: 0.55rem;
  padding: 0.55rem 0.7rem;
  color: #6c757d;
  font-size: 0.8rem;
  background: #f8f9fa;
}

.device-ranking-list {
  display: grid;
  gap: 0.75rem;
}

.device-ranking-item {
  border: 1px solid #e3e7eb;
  border-radius: 0.7rem;
  background: #ffffff;
  padding: 0.75rem 0.8rem;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.device-ranking-leader {
  border-color: rgba(25, 135, 84, 0.42);
  box-shadow: 0 0 0 1px rgba(25, 135, 84, 0.12);
}

.device-ranking-attention {
  border-color: rgba(220, 53, 69, 0.35);
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.09);
}

.ranking-header-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.ranking-device-block {
  display: flex;
  align-items: center;
  gap: 0.58rem;
  min-width: 220px;
}

.rank-badge {
  min-width: 1.55rem;
  height: 1.55rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  border: 1px solid transparent;
}

.rank-badge-gold {
  color: #9a6700;
  background: rgba(255, 193, 7, 0.24);
  border-color: rgba(255, 193, 7, 0.5);
}

.rank-badge-silver {
  color: #334155;
  background: rgba(148, 163, 184, 0.2);
  border-color: rgba(148, 163, 184, 0.45);
}

.rank-badge-bronze {
  color: #92400e;
  background: rgba(251, 146, 60, 0.18);
  border-color: rgba(251, 146, 60, 0.5);
}

.rank-badge-default {
  color: #475569;
  background: rgba(226, 232, 240, 0.7);
  border-color: rgba(203, 213, 225, 0.8);
}

.ranking-device-name {
  font-weight: 700;
  font-size: 0.96rem;
  line-height: 1.25;
}

.ranking-device-id {
  font-size: 0.72rem;
  color: #6c757d;
}

.ranking-totals-block {
  text-align: right;
}

.ranking-total-revenue {
  font-weight: 700;
  font-size: 1.03rem;
  line-height: 1.2;
}

.ranking-total-photos {
  font-size: 0.74rem;
  color: #6b7280;
}

.ranking-metrics-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.38rem;
  margin-top: 0.45rem;
}

.ranking-metric-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  font-size: 0.71rem;
  line-height: 1.2;
}

.ranking-metric-pill strong {
  color: #1f2937;
  font-weight: 700;
}

.growth-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #6c757d;
}

.growth-badge {
  font-size: 0.72rem;
  padding: 0.18rem 0.48rem;
  border-radius: 0.4rem;
  border: 1px solid transparent;
  font-weight: 700;
  line-height: 1.28;
}

.growth-badge-up {
  color: #166534;
  background: rgba(34, 197, 94, 0.14);
  border-color: rgba(34, 197, 94, 0.35);
}

.growth-badge-down {
  color: #b91c1c;
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.32);
}

.growth-badge-flat {
  color: #475569;
  background: rgba(148, 163, 184, 0.14);
  border-color: rgba(148, 163, 184, 0.28);
}

.rank-chip {
  font-size: 0.66rem;
  line-height: 1.3;
  border-radius: 999px;
  padding: 0.14rem 0.44rem;
  font-weight: 700;
  border: 1px solid transparent;
}

.rank-chip-success {
  color: #198754;
  background: rgba(25, 135, 84, 0.09);
  border-color: rgba(25, 135, 84, 0.28);
}

.rank-chip-warning {
  color: #b45309;
  background: rgba(245, 158, 11, 0.14);
  border-color: rgba(245, 158, 11, 0.3);
}

.ranking-sparkline-wrap {
  width: 100%;
  min-height: 44px;
}

.ranking-sparkline {
  display: block;
  width: 100%;
  height: 46px;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border-radius: 0.5rem;
  border: 1px solid #e6ebf0;
}

.ranking-sparkline-hitpoint {
  fill: transparent;
  stroke: transparent;
}

.summary-title {
  font-size: 0.82rem;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.summary-value {
  font-size: 1.2rem;
  font-weight: 700;
}

.share-progress {
  height: 6px;
}

.price-insight-device:last-child {
  margin-bottom: 0 !important;
}

.price-insight-controls {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.65rem;
}

.price-insight-collapsed-note {
  border: 1px dashed #ced4da;
  border-radius: 0.5rem;
  background: #f8f9fa;
  color: #6c757d;
  font-size: 0.82rem;
  padding: 0.55rem 0.7rem;
}

.calculation-modal-body {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 0.1rem;
}

.info-section {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  background: #f8f9fa;
}

.info-pre {
  font-size: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.5rem;
}

@media (max-width: 768px) {
  .analytics-page {
    padding: 0 2px;
  }

  .analytics-empty {
    min-height: 120px;
  }

  .summary-value {
    font-size: 1.05rem;
  }

  .calculation-modal-body {
    max-height: 64vh;
  }

  .ranking-toolbar {
    align-items: flex-start;
  }

  .ranking-toolbar-left {
    width: 100%;
  }

  .ranking-toggle-wrap {
    width: 100%;
    justify-content: flex-start;
  }

  .ranking-device-block {
    min-width: 0;
  }

  .ranking-header-row {
    gap: 0.45rem;
  }

  .ranking-totals-block {
    text-align: left;
  }

  .ranking-metrics-row {
    gap: 0.3rem;
  }

  .device-ranking-item {
    padding: 0.55rem 0.6rem;
  }

  .ranking-sparkline {
    height: 48px;
  }
}
</style>
